import { useMemo } from 'react';
import { withNamespaces } from 'react-i18next';
import { Card, CardBody, CardTitle, Col, Row } from 'reactstrap';

import StructureComparisonColumn from './components/StructureComparisonColumn';
import { buildProviderSourceColumnHeader } from './utils/columnHeaders';
import { hasValidShareholderGraph } from './utils/hasValidShareholderGraph';
import { transformShareholdersToGraph } from './utils/transformShareholdersToGraph';
import useSupplierOverviewCorporateStructureComparison from './hooks/useSupplierOverviewCorporateStructureComparison';
import useSupplierOverviewShareholdersGraph from './hooks/useSupplierOverviewShareholdersGraph';

const CorporateStructureComparison = ({
    t,
    supplierId,
    hasKYBFeature
}) => {
    const fetchCorporateStructureComparisonQuery = useSupplierOverviewCorporateStructureComparison({
        supplierId,
        t,
        enabled: !!hasKYBFeature,
    });

    const fetchShareholdersGraphQuery = useSupplierOverviewShareholdersGraph({
        supplierId,
        t,
    });

    const {
        source = null,
    } = fetchCorporateStructureComparisonQuery.data || {};

    const isSourceLoading = fetchCorporateStructureComparisonQuery.isLoading
        || fetchCorporateStructureComparisonQuery.isFetching;

    const isTargetLoading = fetchShareholdersGraphQuery.isLoading
        || fetchShareholdersGraphQuery.isFetching;

    const sourceHeader = source?.providerName
        ? buildProviderSourceColumnHeader(source.providerName, t)
        : '';

    const sourceGraphData = useMemo(() => {
        if (!source || !hasValidShareholderGraph(source.shareholderGraph)) {
            return null;
        }

        return source.shareholderGraph;
    }, [source]);

    const targetGraphData = useMemo(
        () => transformShareholdersToGraph(fetchShareholdersGraphQuery.data?.shareHolders),
        [fetchShareholdersGraphQuery.data]
    );

    const sourceEmptyMessage = (
        <div className="text-muted">
            <p className="mb-0">
                {t('Corporate structure information is not available yet. Please generate a KYB report from an external provider in the Company Info tab to view this information.')}
            </p>
        </div>
    );

    const targetEmptyMessage = (
        <div className="text-muted">
            <p className="mb-2">
                {t('No shareholder information is available yet.')}
            </p>
            <p className="mb-0">
                {t('Add shareholders in the Shareholders section to display the corporate structure.')}
            </p>
        </div>
    );

    return (
        <Card className="overview-corporate-structure-comparison mb-4">
            <CardBody>
                <CardTitle tag="h6" className="overview-section-title mb-4">
                    {t('Corporate Structure Comparison')}
                </CardTitle>

                <Row className="g-0 align-items-stretch">
                    {hasKYBFeature && (
                        <Col
                            md="6"
                            className="overview-corporate-structure-comparison__column-wrapper overview-corporate-structure-comparison__column-wrapper--left pe-md-4"
                        >
                            <StructureComparisonColumn
                                header={sourceHeader}
                                graphData={sourceGraphData}
                                isLoading={isSourceLoading}
                                emptyMessage={
                                    !isSourceLoading && !fetchCorporateStructureComparisonQuery.isError
                                        ? sourceEmptyMessage
                                        : null
                                }
                            />
                        </Col>
                    )}

                    <Col
                        md={hasKYBFeature ? '6' : '12'}
                        className={`overview-corporate-structure-comparison__column-wrapper overview-corporate-structure-comparison__column-wrapper--right${hasKYBFeature ? ' ps-md-4' : ''}`}
                    >
                        <StructureComparisonColumn
                            header={t('Source: Due Diligence Report submitted by the business partner')}
                            graphData={targetGraphData}
                            isLoading={isTargetLoading}
                            emptyMessage={
                                !isTargetLoading && !fetchShareholdersGraphQuery.isError
                                    ? targetEmptyMessage
                                    : null
                            }
                        />
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};

export default withNamespaces()(CorporateStructureComparison);
